import React, { forwardRef, ButtonHTMLAttributes } from 'react';
import { useTheme } from '../layout/ThemeProvider';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'ghost' | 'cosmic' | 'gradient';
  size?: 'sm' | 'md' | 'lg' | 'xl';
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
  loading?: boolean;
  glow?: boolean;
  children: React.ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  variant = 'primary',
  size = 'md',
  icon,
  iconPosition = 'left',
  loading = false,
  glow = false,
  disabled,
  className = '',
  children,
  ...props
}, ref) => {
  const { isDark } = useTheme();

  const sizeClasses = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg',
    xl: 'px-10 py-5 text-xl',
  };

  const getVariantClasses = () => {
    const baseClasses = 'relative overflow-hidden font-medium rounded-xl transition-all duration-300 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

    switch (variant) {
      case 'primary':
        return `${baseClasses} btn-primary ${glow ? 'pulse-glow' : ''}`;
      case 'secondary':
        return `${baseClasses} btn-secondary`;
      case 'ghost':
        return `${baseClasses} bg-transparent hover:bg-opacity-10 border-0 focus:ring-vibrant-orange`;
      case 'cosmic':
        return `${baseClasses} bg-gradient-to-r from-deep-space-purple to-muted-purple text-astronaut-white hover:from-muted-purple hover:to-deep-space-purple focus:ring-muted-purple`;
      case 'gradient':
        return `${baseClasses} bg-button-orange text-astronaut-white hover:shadow-glow-orange-strong focus:ring-vibrant-orange transform hover:scale-105 ${glow ? 'pulse-glow' : ''}`;
      default:
        return `${baseClasses} btn-primary`;
    }
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
    xl: 'w-7 h-7',
  };

  const LoadingSpinner = () => (
    <div className={`animate-spin rounded-full border-2 border-current border-t-transparent ${iconSizes[size]}`} />
  );

  const RocketIcon = () => (
    <span className={`${iconSizes[size]} animate-rocket-float flex items-center justify-center text-2xl`}>
      🛸
    </span>
  );

  const renderIcon = () => {
    if (loading) return null;
    if (icon) return <div className={iconSizes[size]}>{icon}</div>;
    if (variant === 'cosmic') return <RocketIcon />;
    return null;
  };

  const iconElement = renderIcon();

  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={`${getVariantClasses()} ${sizeClasses[size]} ${className}`}
      {...props}
    >
      {/* Button Glow Effect Overlay */}
      {(variant === 'primary' || variant === 'cosmic') && !disabled && (
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 hover:opacity-20 transition-opacity duration-300 transform -skew-x-12" />
      )}

      {/* Button Content */}
      <span className="relative flex items-center justify-center space-x-2">
        {iconElement && iconPosition === 'left' && (
          <span className={children ? 'mr-2' : ''}>{iconElement}</span>
        )}
        
        {children && (
          <span className={loading ? 'opacity-0' : ''}>{children}</span>
        )}
        
        {iconElement && iconPosition === 'right' && (
          <span className={children ? 'ml-2' : ''}>{iconElement}</span>
        )}
      </span>

      {/* Loading overlay */}
      {loading && (
        <div className="absolute inset-0 flex items-center justify-center">
          <LoadingSpinner />
        </div>
      )}
    </button>
  );
});

Button.displayName = 'Button';

// Icon Button Component
export interface IconButtonProps extends Omit<ButtonProps, 'children'> {
  'aria-label': string;
  icon: React.ReactNode;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(({
  icon,
  size = 'md',
  variant = 'ghost',
  className = '',
  ...props
}, ref) => {
  const sizeClasses = {
    sm: 'p-2',
    md: 'p-3',
    lg: 'p-4',
    xl: 'p-5',
  };

  return (
    <Button
      ref={ref}
      variant={variant}
      size={size}
      icon={icon}
      className={`!px-0 !py-0 ${sizeClasses[size]} ${className}`}
      {...props}
    >
      <span className="sr-only">{props['aria-label']}</span>
    </Button>
  );
});

IconButton.displayName = 'IconButton';

// Floating Action Button
export interface FABProps extends Omit<ButtonProps, 'variant' | 'size'> {
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
}

export const FloatingActionButton = forwardRef<HTMLButtonElement, FABProps>(({
  position = 'bottom-right',
  className = '',
  children,
  ...props
}, ref) => {
  const positionClasses = {
    'bottom-right': 'fixed bottom-6 right-6',
    'bottom-left': 'fixed bottom-6 left-6',
    'top-right': 'fixed top-6 right-6',
    'top-left': 'fixed top-6 left-6',
  };

  return (
    <Button
      ref={ref}
      variant="cosmic"
      size="lg"
      glow
      className={`
        ${positionClasses[position]} 
        !rounded-full w-16 h-16 !p-0 z-50
        shadow-2xl hover:scale-110 transform transition-transform duration-300
        ${className}
      `}
      {...props}
    >
      {children}
    </Button>
  );
});

FloatingActionButton.displayName = 'FloatingActionButton';

// Button Group Component
export interface ButtonGroupProps {
  children: React.ReactNode;
  orientation?: 'horizontal' | 'vertical';
  size?: ButtonProps['size'];
  variant?: ButtonProps['variant'];
  className?: string;
}

export const ButtonGroup: React.FC<ButtonGroupProps> = ({
  children,
  orientation = 'horizontal',
  size,
  variant,
  className = '',
}) => {
  const orientationClasses = {
    horizontal: 'flex-row space-x-0',
    vertical: 'flex-col space-y-0',
  };

  // Clone children to apply group styles
  const clonedChildren = React.Children.map(children, (child, index) => {
    if (React.isValidElement<ButtonProps>(child) && child.type === Button) {
      const isFirst = index === 0;
      const isLast = index === React.Children.count(children) - 1;
      
      let groupClasses = '';
      if (orientation === 'horizontal') {
        if (isFirst) groupClasses = '!rounded-r-none border-r-0';
        else if (isLast) groupClasses = '!rounded-l-none';
        else groupClasses = '!rounded-none border-r-0';
      } else {
        if (isFirst) groupClasses = '!rounded-b-none border-b-0';
        else if (isLast) groupClasses = '!rounded-t-none';
        else groupClasses = '!rounded-none border-b-0';
      }

      return React.cloneElement(child, {
        size: size || child.props.size,
        variant: variant || child.props.variant,
        className: `${child.props.className || ''} ${groupClasses}`,
      } as Partial<ButtonProps>);
    }
    return child;
  });

  return (
    <div className={`inline-flex ${orientationClasses[orientation]} ${className}`}>
      {clonedChildren}
    </div>
  );
};

export default Button; 